import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useStoreStatus } from "@/context/StoreStatusContext";
import { Flame, Minus, Plus, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { cldImage } from "@/lib/cldImage";

const FLAVORS = ["Traditional Italian", "Biscoff", "Pistachio", "Nutella"];
const SIGNATURE = ["Traditional Italian", "Biscoff", "Pistachio", "Nutella"];
const BOX_IMG = cldImage("https://images.unsplash.com/photo-1571115177098-24ec42ed204d", { w: 1400 });

// Bundle discount by size — must mirror BOX_BUNDLE_DISCOUNTS on the backend.
const BUNDLE_RATES = { 2: 0.07, 4: 0.12, 6: 0.15 };

// Fallback per-cup pricing used until the products API responds. Prevents the
// pricing block, savings banner and Total from rendering as ₹0 during the
// first paint on slow networks. Values mirror the live DB — safe defaults.
const FALLBACK_CUP_PRICES = {
  "Traditional Italian": 279,
  "Biscoff": 289,
  "Pistachio": 319,
  "Nutella": 289,
};

export default function BoxBuilder() {
  const { size } = useParams();
  const boxSize = parseInt(size, 10);
  const navigate = useNavigate();
  const { addItem } = useCart();
  const { storeStatus } = useStoreStatus();
  const storeOpen = !storeStatus || storeStatus.is_open;
  const [box, setBox] = useState(null);
  const [cupPrices, setCupPrices] = useState({});
  const [counts, setCounts] = useState({});
  const [popular, setPopular] = useState(null); // {combo, order_count, source}

  useEffect(() => {
    api.get("/boxes").then(({ data }) => {
      const found = data.find((b) => b.size === boxSize);
      setBox(found);
      if (boxSize === 1) setCounts({ "Traditional Italian": 1 });
    }).catch(() => {});
    // Pull live cup prices so the picker computes real per-flavour totals.
    api.get("/products", { params: { category: "cups", available_only: true } })
      .then(({ data }) => {
        const m = {};
        for (const p of data) m[p.flavor || p.name] = p.price;
        setCupPrices(m);
      })
      .catch(() => {});
    // "Most ordered mix" auto-suggest — only meaningful for multi-cup boxes.
    if (boxSize === 4 || boxSize === 6) {
      api.get("/boxes/popular-mix", { params: { size: boxSize } })
        .then(({ data }) => setPopular(data))
        .catch(() => {});
    }
  }, [boxSize]);

  const total = Object.values(counts).reduce((a, b) => a + b, 0);
  const remaining = boxSize - total;
  const rate = BUNDLE_RATES[boxSize] || 0;
  const ratePct = Math.round(rate * 100);

  const { baseTotal, savings, bundlePrice } = useMemo(() => {
    const b = Object.entries(counts).reduce(
      (s, [f, n]) => s + n * (cupPrices[f] ?? FALLBACK_CUP_PRICES[f] ?? 279),
      0,
    );
    const sv = Math.round(b * rate);
    return { baseTotal: b, savings: sv, bundlePrice: Math.round(b - sv) };
  }, [counts, cupPrices, rate]);

  const setCount = (f, n) => setCounts((c) => ({ ...c, [f]: Math.max(0, n) }));
  const bump = (f, d) => {
    if (d > 0 && remaining <= 0) return;
    if (boxSize === 1) {
      if (d > 0) setCounts({ [f]: 1 });
      else setCounts({});
      return;
    }
    setCount(f, (counts[f] || 0) + d);
  };

  const applySignature = () => {
    let c = {};
    if (boxSize === 4) SIGNATURE.forEach((f) => (c[f] = 1));
    else if (boxSize === 6) {
      SIGNATURE.forEach((f) => (c[f] = 1));
      c["Traditional Italian"] = (c["Traditional Italian"] || 0) + 1;
      c["Pistachio"] = (c["Pistachio"] || 0) + 1;
    } else if (boxSize === 2) c = { "Traditional Italian": 1, "Pistachio": 1 };
    else c = { "Traditional Italian": 1 };
    setCounts(c);
  };

  const popularSummary = useMemo(() => {
    if (!popular?.combo) return "";
    return Object.entries(popular.combo)
      .map(([f, n]) => (n > 1 ? `${f} ×${n}` : f))
      .join(" · ");
  }, [popular]);

  const applyPopular = () => {
    if (!popular?.combo) return;
    setCounts({ ...popular.combo });
    toast.success("Popular mix loaded — tweak it if you like.");
  };

  const handleAdd = () => {
    if (!storeOpen) {
      toast.error(storeStatus?.closed_message || "The store is closed right now.");
      return;
    }
    if (total !== boxSize) return;
    const selections = Object.entries(counts).flatMap(([f, n]) => Array(n).fill(f));
    const flavor = boxSize === 1 ? selections[0] : undefined;
    addItem({
      id: box.id, slug: box.slug, name: box.title, price: bundlePrice,
      original_price: baseTotal, savings,
      image_url: BOX_IMG, category: "boxes",
    }, { selections, flavor });
    toast.success(`${box.title} added — layered with care.`);
    navigate("/cart");
  };

  if (!box) return <div className="max-w-[900px] mx-auto px-6 py-32 text-espresso-light">Loading…</div>;

  const isFull = total === boxSize;
  const bundleEnabled = rate > 0;

  return (
    <main data-testid="box-builder" className="max-w-[1200px] mx-auto px-6 md:px-10 py-12 md:py-16">
      <Link to="/" className="label-xs text-espresso-light link-underline" data-testid="box-back">← Back</Link>

      <div className="mt-6 grid md:grid-cols-12 gap-10 md:gap-16 items-start">
        <div className="md:col-span-5 md:sticky md:top-28">
          <div className="aspect-[4/5] overflow-hidden bg-ivory-100">
            <img src={BOX_IMG} alt={box.title} className="w-full h-full object-cover" />
          </div>
          <div className="mt-6 label-xs text-gold">{box.sub}</div>
          <h1 className="mt-2 font-serif text-4xl md:text-5xl leading-[0.95] text-espresso font-normal">
            {box.title}
          </h1>
          <p className="mt-4 text-espresso-light leading-relaxed">{box.desc}</p>
          {bundleEnabled && (
            <div
              data-testid="bundle-rate-tag"
              className="mt-5 inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-gold/40 bg-gold/10 text-gold-hover label-xs"
            >
              <Sparkles size={12} strokeWidth={1.75} />
              Bundle price — {ratePct}% off
            </div>
          )}
        </div>

        <div className="md:col-span-7">
          <div className="label-xs text-espresso-light">Tiramisù, your way</div>
          <h2 className="mt-3 font-serif text-3xl md:text-4xl leading-tight text-espresso">
            {boxSize === 1 ? "Choose your flavour." : "Mix your flavours."}
          </h2>

          <div className="mt-6 flex items-baseline justify-between border-b border-line pb-4">
            <div className="label-xs text-espresso-light">Selections</div>
            <div
              data-testid="selections-count"
              className={`font-serif text-lg ${isFull ? "text-gold-hover" : "text-espresso-light"}`}
            >
              {total} / {boxSize} {boxSize === 1 ? "chosen" : "cups"} {isFull && "✓"}
            </div>
          </div>

          {popular?.combo && (boxSize === 4 || boxSize === 6) && (
            <button
              type="button"
              onClick={applyPopular}
              data-testid="popular-mix-pill"
              className="mt-5 group w-full flex items-center justify-between gap-3 px-4 py-3 border border-gold/40 bg-gold/5 hover:bg-gold/10 transition-colors text-left"
            >
              <div className="flex items-start gap-3 min-w-0">
                <Flame size={16} strokeWidth={1.75} className="text-gold-hover shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <div className="label-xs text-gold-hover">
                    {popular.source === "orders"
                      ? `Most ordered mix · ${popular.order_count}+ orders`
                      : "Most ordered mix"}
                  </div>
                  <div
                    className="mt-0.5 font-serif text-espresso text-base truncate"
                    data-testid="popular-mix-summary"
                  >
                    {popularSummary}
                  </div>
                </div>
              </div>
              <span className="label-xs text-espresso link-underline shrink-0">
                Fill my box
              </span>
            </button>
          )}

          <div className="mt-4 space-y-2">
            {FLAVORS.map((f) => {
              const qty = counts[f] || 0;
              const active = qty > 0;
              const cupPrice = cupPrices[f];
              const incDisabled = remaining <= 0;
              return (
                <div key={f} data-testid={`flavor-row-${f}`}
                  className={`flex items-center justify-between px-5 py-4 border transition-colors ${active ? "border-espresso bg-ivory-100" : "border-line"}`}>
                  <div>
                    <div className="font-serif text-xl text-espresso">{f}</div>
                    <div className="label-xs text-espresso-light mt-1">
                      ₹{cupPrice ?? FALLBACK_CUP_PRICES[f] ?? 279} per cup{active ? ` · ×${qty}` : ""}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <button onClick={() => bump(f, -1)} disabled={!qty}
                      data-testid={`flavor-dec-${f}`}
                      className="w-10 h-10 rounded-full border border-line flex items-center justify-center disabled:opacity-30 hover:border-espresso transition-colors">
                      <Minus size={16} strokeWidth={1.5} />
                    </button>
                    <span className="w-6 text-center font-serif text-lg" data-testid={`flavor-qty-${f}`}>{qty}</span>
                    <button
                      onClick={() => bump(f, +1)}
                      disabled={incDisabled}
                      data-testid={`flavor-inc-${f}`}
                      title={incDisabled ? "Box is full — remove a flavour to swap" : ""}
                      className="w-10 h-10 rounded-full border border-line flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed hover:border-espresso transition-colors">
                      <Plus size={16} strokeWidth={1.5} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {boxSize > 1 && (
            <button onClick={applySignature}
              data-testid="signature-btn"
              className="mt-6 label-xs link-underline text-espresso">
              Not sure? Let us choose for you →
            </button>
          )}

          {/* Live pricing block — always mounted (no responsive hiding) so mobile
              and desktop mirror the /box/1 layout exactly. */}
          <div className="mt-10 border-t border-line pt-6 space-y-3">
            {bundleEnabled && baseTotal > 0 && (
              <>
                <div className="flex items-baseline justify-between text-espresso-light" data-testid="bundle-original">
                  <span className="label-xs">Cups total</span>
                  <span className="font-serif text-lg line-through">₹{baseTotal}</span>
                </div>
                <div
                  data-testid="bundle-savings"
                  className={`flex items-baseline justify-between px-4 py-3 rounded-md ${isFull ? "bg-gold/15 border border-gold/40" : "bg-ivory-100"}`}
                >
                  <span className="label-xs text-gold-hover flex items-center gap-1.5">
                    <Sparkles size={12} strokeWidth={1.75} />
                    {isFull ? `Awesome! You saved` : "Total savings"}
                  </span>
                  <span className="font-serif text-lg text-gold-hover">
                    −₹{savings}
                  </span>
                </div>
              </>
            )}
            <div className="flex items-center justify-between pt-2 gap-4 flex-wrap">
              <div>
                <div className="label-xs text-espresso-light">Total</div>
                <div className="font-serif text-4xl text-espresso font-normal" data-testid="bundle-total">
                  ₹{bundleEnabled ? bundlePrice : baseTotal}
                </div>
              </div>
              <button
                onClick={handleAdd}
                data-testid="box-add-btn"
                aria-disabled={!isFull || !storeOpen}
                disabled={!isFull || !storeOpen}
                className="btn-espresso disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {!storeOpen ? "Closed today" : (isFull ? "Add to Cart" : `Choose ${remaining} more`)}
              </button>
            </div>
          </div>

          {boxSize < 6 && (
            <p className="mt-6 font-serif italic text-espresso-light text-center md:text-left">
              {boxSize === 1 && "Two cups make it a moment for two →"}
              {boxSize === 2 && "Two more cups and it becomes a Sharing Box →"}
              {boxSize === 4 && "Two more and it's ready for the occasion →"}
              {" "}
              <Link to={`/box/${boxSize === 1 ? 2 : boxSize === 2 ? 4 : 6}`} className="link-underline text-espresso not-italic label-xs" data-testid="box-upgrade-link">Upgrade</Link>
            </p>
          )}
        </div>
      </div>
    </main>
  );
}
